import {Accordion, Col, Row, Form, Button, InputGroup, ProgressBar} from "react-bootstrap";
import {ADMIN_LANG} from "../../../../settings";
import React, {ChangeEvent, useState} from "react";
import {PlainDetails} from "../../../../types/admin";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import AsyncThemeSelect from "../../gameView/AsyncThemeSelect";
import {changeGameModal, uploadGameImage} from "../../../../app/slices/modals/gameEditModal";
import RichTextField from "../../RichTextField";
import {Upload} from "react-bootstrap-icons";
import {openModal as openThemeModal} from "../../../../app/slices/modals/themeModal";
import {AsyncOptions} from "../AsyncOptions";

type Props = {
    languageTab: string;
}

const ThemeSettings = ({languageTab}: Props) => {
    const dispatch = useAppDispatch();
    const {theme, languages, show_lift, game_id, isUploading, theme_options} = useAppSelector((state) => state.gameEditModal);
    const [file, setFile] = useState<File | null>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = () => {
        if (!file || !game_id) return;
        let formData = new FormData();
        formData.append("game_id", game_id);
        formData.append("file", file);
        formData.append("language_code", languageTab);

        dispatch(uploadGameImage(formData));
    };

    const handleChangeImage = () => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return { ...language, image: undefined };
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
        setFile(null)
    }

    const handleExcerptChange = (excerpt: string) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, excerpt};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleShowLiftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeGameModal({show_lift: e.target.checked}));
    }

    const handleThemeChange = (selectedValue: PlainDetails) => {
        dispatch(changeGameModal({theme: selectedValue}));
    }

    const handleAddNewTheme = () => {
        if(game_id) {
            dispatch(openThemeModal({theme_id: null, game_id}));
        }
    }

    const handleViewTheme = (item: PlainDetails) => {
        if(game_id && item){
            dispatch(openThemeModal({theme_id: item.value, game_id}));
        }
    }

    const handleRemoveTheme = () => {
        dispatch(changeGameModal({theme: null}));
    }

    const languageImage = languages.find((lang) => lang.language_code === languageTab)?.image;

    return (
        <Accordion.Body>
            <Row className='gy-3'>
                <Col xs={12}>
                    <Form.Group controlId={`theme_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.theme}</Form.Label>
                        <div className='d-flex align-items-center'>
                            <div className="flex-grow-1">
                                <AsyncThemeSelect onChange={handleThemeChange} initialValue={theme} defaultOptions={theme_options}/>
                            </div>
                            <AsyncOptions item={theme} handleAddNew={handleAddNewTheme} handleViewItem={handleViewTheme} handleRemoveSelection={handleRemoveTheme} />
                        </div>
                        <Form.Text className='help-text'
                                   dangerouslySetInnerHTML={{__html: ADMIN_LANG.theme_help}}></Form.Text>
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group className='d-flex flex-column' controlId={`image_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.image}</Form.Label>
                        {!languageImage?.thumbnail_url ? (
                        <>
                        <InputGroup>
                            <Form.Control onChange={handleFileChange} type='file' accept='image/jpeg,image/png,image/jpg'/>
                            <Button variant='success' disabled={isUploading || file === null} onClick={handleUpload}><Upload /> {ADMIN_LANG.upload_image}</Button>
                        </InputGroup>
                        {isUploading &&
                            <ProgressBar className='mt-1 mb-1' animated  striped variant="secondary" now={100} />
                        }
                        <Form.Text className='help-text' dangerouslySetInnerHTML={{__html: ADMIN_LANG.lift_image_help}}></Form.Text>
                        </>
                        ) : (
                            <div className='d-flex align-items-center'>
                                <img className='rounded' src={languageImage?.thumbnail_url} width='200px' alt='preview thumbnail'/>
                                <Button onClick={handleChangeImage} className='ms-3'
                                        variant='secondary'>{ADMIN_LANG.change}</Button>
                            </div>
                        )}
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <Form.Group controlId={`excerpt_${languageTab}`} className='pb-3'>
                        <Form.Label className='mb-0'>{`${ADMIN_LANG.excerpt} (${languageTab.toUpperCase()})`}</Form.Label>
                        <Form.Text className='help-text'>{ADMIN_LANG.excerpt_help}</Form.Text>
                        <RichTextField value={languages.find((lang) => lang.language_code === languageTab)?.excerpt || ''}
                                   onChange={handleExcerptChange}/>
                </Form.Group>
                <Form.Group controlId={`show_lift_${languageTab}`}>
                    <Form.Switch
                    className='form-label mb-0'
                    onChange={handleShowLiftChange}
                    checked={show_lift}
                    label={ADMIN_LANG.show_lift}
                    />
                    <Form.Text className='help-text'>{ADMIN_LANG.show_lift_help}</Form.Text>
                </Form.Group>
            </Col>
            </Row>
        </Accordion.Body>

    )
}

export default ThemeSettings;